// Per-state enrolment: who has this state's data, who could, who cannot yet.
//
// Expanded from a state row rather than shown inline, because the interesting case is a
// handful of named districts against a table of fourteen states — putting the names in
// the grid would bury them.
//
// Enrolling is a real action: it creates a data source and makes a dataset visible to a
// district. So it is two steps, not one. "Check" runs the server's dry run and reports
// exactly what would happen; only then does "Enrol" appear. That mirrors the endpoint,
// which defaults to dry_run and requires the destructive form to be asked for, and it
// means the common failures — no storage container, no combined outputs for the state —
// surface as a readable sentence before anything is written rather than as a 500.
//
// The real enrol answers "queued": the server records the source at once and copies the
// files in a job of its own (copying Kentucky inside the API took it down on 2026-10-16),
// so this panel polls the job until it ends. An enrolled district shows how many of the
// state's datasets are registered, and "Finish" when it is short — the row that used to
// read as done while holding 17 of 113.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Divider, IconButton, List, ListItem, Tooltip,
    Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import { enrollClient, getEnrolJob, getEnrollment } from './api';

const OK = '#2e7d32';
const WARN = '#ed6c02';
const BAD = '#c62828';
const POLL_MS = 10000;

const Row = ({ children, sx }) => (
    <ListItem disableGutters sx={{ py: 0.4, display: 'flex', gap: 1, ...sx }}>{children}</ListItem>
);

// The sentence for a finished enrol, from the result the server returns (inline or via job).
// A refresh of an enrolled district copies every output again, so the copy's own counts
// lead: "3 updated, 110 unchanged" is the answer to "did anything change?".
export const describe = (res) => {
    const steps = res?.steps || [];
    const copy = steps.map((s) => s.copy).find((c) => c && typeof c === 'object');
    const reg = steps.map((s) => s.register).filter(Boolean).pop();
    const parts = [];
    if (copy && typeof copy.uploaded === 'number') {
        parts.push(`${copy.uploaded} updated, ${copy.unchanged} unchanged`);
    }
    if (reg) {
        const failed = reg.failed?.length ? `, ${reg.failed.length} failed` : '';
        parts.push(`${reg.registered} registered, ${reg.already} already there${failed}`);
    }
    return parts.join(' · ') || 'enrolled';
};

// Why a job failed, in one line: the job's own result if it printed one, else Kubernetes'
// reason, else the last log line.
export const failureDetail = (job) => {
    if (job?.result?.reason) return job.result.reason;
    if (job?.result?.status === 'partial') return describe(job.result);
    const last = (job?.log || '').trim().split('\n').filter(Boolean).pop();
    return [job?.reason, job?.message, last].filter(Boolean).join(' — ') || 'the enrol job failed';
};

const EnrolmentPanel = ({ stateName }) => {
    const [data, setData] = useState(null);
    const [listedAt, setListedAt] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    // Per-client: { phase: 'checking'|'checked'|'enrolling'|'done'|'error', detail }
    const [work, setWork] = useState({});
    const timers = useRef({});

    // `refresh` relists the state's outputs on the server (otherwise a ten-minute cache);
    // who is enrolled is read live either way. The first load shows the spinner; a
    // refresh keeps the list on screen and spins the button, so nothing jumps.
    const load = useCallback(async (refresh = false) => {
        if (refresh) setRefreshing(true); else setLoading(true);
        setError(null);
        try {
            const r = await getEnrollment(refresh);
            setData(r.states?.[stateName] || { enrolled: [], eligible: [], blocked: [] });
            setListedAt(r.outputs_listed_at || null);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [stateName]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), []);

    const setPhase = (id, phase, detail) => setWork((w) => ({ ...w, [id]: { phase, detail } }));

    // Poll a queued enrol until the job ends. Progress is the job's last log line, which
    // names the file being copied, so a 20-minute Kentucky copy does not look hung.
    const watch = (id, jobName) => {
        const tick = async () => {
            try {
                const job = await getEnrolJob(jobName);
                if (job.phase === 'succeeded') {
                    setPhase(id, 'done', describe(job.result));
                    load();   // enrolling moves them out of "eligible"; finishing updates the count
                    return;
                }
                if (job.phase === 'failed') {
                    setPhase(id, 'error', failureDetail(job));
                    load();
                    return;
                }
                const last = (job.log || '').trim().split('\n').filter(Boolean).pop();
                setPhase(id, 'enrolling', job.phase === 'queued' ? 'waiting for a pod' : (last || 'copying'));
            } catch (e) {
                setPhase(id, 'enrolling', `checking… (${e.message})`);
            }
            timers.current[id] = setTimeout(tick, POLL_MS);
        };
        timers.current[id] = setTimeout(tick, POLL_MS);
    };

    const run = async (client, dryRun) => {
        const id = client.client_id;
        setPhase(id, dryRun ? 'checking' : 'enrolling');
        try {
            const res = await enrollClient(stateName, id, dryRun);
            if (res.status === 'refused' || res.status === 'error') {
                setPhase(id, 'error', res.reason);
                return;
            }
            if (dryRun) {
                setPhase(id, 'checked', `${res.outputs} output(s) would be copied and registered`);
            } else if (res.status === 'queued') {
                setPhase(id, 'enrolling', 'queued');
                watch(id, res.job);
            } else {
                setPhase(id, 'done', describe(res));
                load();
            }
        } catch (e) {
            setPhase(id, 'error', e.message);
        }
    };

    if (loading) return <Box sx={{ py: 2 }}><CircularProgress size={18} /></Box>;
    if (error) return <Alert severity="error" sx={{ my: 1 }}>{error}</Alert>;

    const { enrolled = [], eligible = [], blocked = [], outputs } = data || {};

    const Progress = ({ w }) => (
        <>
            <CircularProgress size={14} />
            {w.detail && (
                <Typography className="d-font" sx={{ fontSize: 11.5, color: '#666' }}>{w.detail}</Typography>
            )}
        </>
    );

    return (
        <Box sx={{ py: 1.5, pl: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography className="d-font" sx={{ fontSize: 12, fontWeight: 700, color: '#555' }}>
                    Enrolled · {enrolled.length}
                </Typography>
                <Tooltip title="Re-read who is enrolled and relist the state's outputs in SharePoint.">
                    <span>
                        <IconButton size="small" onClick={() => load(true)} disabled={refreshing}
                            aria-label="refresh enrolment" sx={{ p: 0.25 }}>
                            {refreshing
                                ? <CircularProgress size={12} />
                                : <RefreshIcon sx={{ fontSize: 15, color: '#777' }} />}
                        </IconButton>
                    </span>
                </Tooltip>
                {typeof outputs === 'number' && (
                    <Typography className="d-font" sx={{ fontSize: 11, color: '#999' }}>
                        {outputs} outputs{listedAt ? `, listed ${new Date(listedAt).toLocaleTimeString()}` : ''}
                    </Typography>
                )}
            </Box>
            {enrolled.length === 0 && (
                <Typography className="d-font" sx={{ fontSize: 12.5, color: '#999', mb: 1 }}>
                    No district in this state is receiving it.
                </Typography>
            )}
            <List dense disablePadding sx={{ mb: eligible.length || blocked.length ? 1.5 : 0 }}>
                {enrolled.map((c) => {
                    const w = work[c.client_id] || {};
                    const total = c.outputs ?? outputs;
                    const known = typeof c.registered === 'number' && typeof total === 'number';
                    const short = known && c.registered < total;
                    return (
                        <Row key={c.client_id}>
                            <CheckCircleIcon sx={{ fontSize: 15, color: short ? WARN : OK }} />
                            <Typography className="d-font" sx={{ fontSize: 12.5, minWidth: 230 }}>{c.title}</Typography>
                            {known && (
                                <Tooltip title="Datasets registered in Nexus out of the state's outputs.">
                                    <Chip size="small" label={`${c.registered} / ${total} registered`}
                                        sx={{ height: 20, fontSize: 11,
                                            bgcolor: short ? '#fff3e0' : '#e8f5e9', color: short ? WARN : OK }} />
                                </Tooltip>
                            )}
                            {w.phase === 'enrolling' ? (
                                <Progress w={w} />
                            ) : (
                                <>
                                    {w.phase === 'done' && (
                                        <Chip size="small" label={w.detail}
                                            sx={{ height: 20, fontSize: 11, bgcolor: '#e8f5e9', color: OK }} />
                                    )}
                                    {short && w.phase !== 'done' && (
                                        <Tooltip title="Copies the outputs this district is missing and registers them. Nothing already there is touched.">
                                            <Button size="small" variant="outlined"
                                                sx={{ textTransform: 'none', fontSize: 11.5, py: 0, minWidth: 64 }}
                                                onClick={() => run(c, false)}>
                                                Finish
                                            </Button>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Refresh this district's data now: re-copy the state's current outputs to it and register anything missing. Files that have not changed are skipped.">
                                        <IconButton size="small" onClick={() => run(c, false)}
                                            aria-label={`refresh ${c.title}`} sx={{ p: 0.25 }}>
                                            <RefreshIcon sx={{ fontSize: 15, color: '#777' }} />
                                        </IconButton>
                                    </Tooltip>
                                </>
                            )}
                            {w.phase === 'error' && (
                                <Typography className="d-font" sx={{ fontSize: 11.5, color: BAD }}>{w.detail}</Typography>
                            )}
                        </Row>
                    );
                })}
            </List>

            {eligible.length > 0 && (
                <>
                    <Divider sx={{ my: 1 }} />
                    <Typography className="d-font" sx={{ fontSize: 12, fontWeight: 700, color: WARN, mb: 0.5 }}>
                        Eligible, not enrolled · {eligible.length}
                    </Typography>
                    <List dense disablePadding>
                        {eligible.map((c) => {
                            const w = work[c.client_id] || {};
                            return (
                                <Row key={c.client_id}>
                                    <Typography className="d-font" sx={{ fontSize: 12.5, minWidth: 230 }}>
                                        {c.title}
                                    </Typography>
                                    {w.phase === 'checking' ? (
                                        <CircularProgress size={14} />
                                    ) : w.phase === 'enrolling' ? (
                                        <Progress w={w} />
                                    ) : w.phase === 'done' ? (
                                        <Chip size="small" label={w.detail}
                                            sx={{ height: 20, fontSize: 11, bgcolor: '#e8f5e9', color: OK }} />
                                    ) : (
                                        <>
                                            <Button size="small" variant="outlined"
                                                sx={{ textTransform: 'none', fontSize: 11.5, py: 0, minWidth: 64 }}
                                                onClick={() => run(c, true)}>
                                                Check
                                            </Button>
                                            {w.phase === 'checked' && (
                                                <Tooltip title="Creates the data source, then copies and registers the state's files in the background. The source stays hidden from the district until you turn on visibility in Data Configuration.">
                                                    <Button size="small" variant="contained"
                                                        sx={{ textTransform: 'none', fontSize: 11.5, py: 0, minWidth: 64 }}
                                                        onClick={() => run(c, false)}>
                                                        Enrol
                                                    </Button>
                                                </Tooltip>
                                            )}
                                        </>
                                    )}
                                    {w.detail && w.phase !== 'done' && w.phase !== 'enrolling' && (
                                        <Typography className="d-font"
                                            sx={{ fontSize: 11.5, color: w.phase === 'error' ? BAD : '#666' }}>
                                            {w.detail}
                                        </Typography>
                                    )}
                                </Row>
                            );
                        })}
                    </List>
                </>
            )}

            {blocked.length > 0 && (
                <>
                    <Divider sx={{ my: 1 }} />
                    <Typography className="d-font" sx={{ fontSize: 12, fontWeight: 700, color: BAD, mb: 0.5 }}>
                        Blocked · {blocked.length}
                    </Typography>
                    <List dense disablePadding>
                        {blocked.map((c) => (
                            <Row key={c.client_id}>
                                <Typography className="d-font" sx={{ fontSize: 12.5, minWidth: 230 }}>
                                    {c.title}
                                </Typography>
                                <Typography className="d-font" sx={{ fontSize: 11.5, color: BAD }}>
                                    {c.reason} — provision it before enrolling
                                </Typography>
                            </Row>
                        ))}
                    </List>
                </>
            )}
        </Box>
    );
};

export default EnrolmentPanel;
